import { useEffect, useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Loader2, Radar, Sparkles, Users, CheckCircle2, MessageSquare, TrendingUp,
  ExternalLink, Phone, Mail, Building2, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Lead {
  id: string;
  nom: string;
  prenom: string;
  entreprise: string;
  email: string;
  telephone?: string;
  poste: string;
  statut: string;
  notes: string;
  tags: string[];
  lh_confidence?: number;
  lh_site?: string;
  lh_ville?: string;
  lh_taille?: string;
  created_at: string;
}

interface Run {
  id: string;
  triggered_by: string;
  started_at: string;
  finished_at?: string;
  count_generated?: number;
  count_inserted?: number;
  duplicates_skipped?: number;
  status: string;
  sector_focus?: string;
  city_focus?: string;
}

interface Stats {
  total_leads: number;
  qualified: number;
  contacted: number;
  runs_total: number;
}

export default function LeadHuntersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [hunting, setHunting] = useState(false);
  const [count, setCount] = useState(10);
  const [sectorFocus, setSectorFocus] = useState("");
  const [cityFocus, setCityFocus] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  const loadAll = async () => {
    try {
      const [sRes, lRes, rRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/leads/stats`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/leads/recent?limit=30`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/leads/runs/recent?limit=10`, { credentials: "include" }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (lRes.ok) { const d = await lRes.json(); setLeads(d.leads || []); }
      if (rRes.ok) { const d = await rRes.json(); setRuns(d.runs || []); }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (user) loadAll(); }, [user]);

  const launchHunt = async () => {
    setHunting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/leads/hunt`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          count,
          sector_focus: sectorFocus.trim() || undefined,
          city_focus: cityFocus.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      if (d.ok) {
        const extras: string[] = [];
        if (d.intros_prepared) extras.push(`${d.intros_prepared} intros prêtes à valider dans Grafton Autonome`);
        if (d.duplicates_skipped) extras.push(`${d.duplicates_skipped} doublons ignorés`);
        toast.success(`${d.inserted} nouveaux leads ajoutés${extras.length ? ` (${extras.join(" · ")})` : ""}`);
        await loadAll();
      } else {
        toast.error(d.error || "La chasse a échoué");
      }
    } catch {
      toast.error("Erreur serveur — réessaye dans un instant");
    }
    setHunting(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return null;

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 space-y-4">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-muted-foreground">
              Ces pistes sont des hypothèses de ciblage générées par l'IA, sans base de données externe : les entreprises, décideurs et e-mails sont à vérifier avant tout contact. Rien n'est envoyé sans votre validation.
            </div>
            <div className="mb-2">
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="lead-hunters-title">
                <Radar className="h-6 w-6 text-primary" /> Lead Hunters — Prospecteur IA
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Grafton IA propose chaque matin des pistes d'entreprises qui correspondent à ton profil client idéal et les ajoute à ton CRM, à vérifier avant tout contact.
              </p>
            </div>

            {/* Hunt control */}
            <Card className="bg-card border-border" data-testid="hunt-control">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" /> Lancer une chasse maintenant
                </CardTitle>
                <CardDescription>
                  Grafton IA propose {count} pistes d'entreprises françaises correspondant à ton profil, sans base externe : à vérifier. ~30s par run.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Nombre de leads</label>
                    <Input
                      type="number"
                      min={1} max={20}
                      value={count}
                      onChange={(e) => setCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 10)))}
                      className="h-9 text-sm"
                      data-testid="hunt-count"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Secteur (optionnel)</label>
                    <Input
                      placeholder="Ex: SaaS B2B, Cabinet d'avocats…"
                      value={sectorFocus}
                      onChange={(e) => setSectorFocus(e.target.value)}
                      className="h-9 text-sm"
                      data-testid="hunt-sector"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Ville (optionnel)</label>
                    <Input
                      placeholder="Ex: Paris, Lyon…"
                      value={cityFocus}
                      onChange={(e) => setCityFocus(e.target.value)}
                      className="h-9 text-sm"
                      data-testid="hunt-city"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={launchHunt} disabled={hunting} className="gap-1.5 w-full h-9" data-testid="launch-hunt-btn">
                      {hunting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
                      {hunting ? "Chasse en cours…" : "Lancer la chasse"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KPI strip */}
            <Card className="bg-card border-border" data-testid="leads-kpi">
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pipeline leads IA</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {stats?.runs_total ?? 0} chasses effectuées
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { label: "Leads dans le CRM", value: stats?.total_leads ?? 0, icon: Users, color: "text-sky-400", testid: "kpi-leads-total" },
                    { label: "Contactés", value: stats?.contacted ?? 0, icon: MessageSquare, color: "text-amber-400", testid: "kpi-leads-contacted" },
                    { label: "Qualifiés / Client", value: stats?.qualified ?? 0, icon: CheckCircle2, color: "text-emerald-400", testid: "kpi-leads-qualified" },
                  ].map((it) => (
                    <div key={it.label} className="border border-border rounded-lg p-3 flex items-center gap-3 bg-background/30" data-testid={it.testid}>
                      <div className={`w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center ${it.color}`}>
                        <it.icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{it.label}</p>
                        <p className="text-xl font-bold leading-none mt-0.5">{it.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent runs */}
            {runs.length > 0 && (
              <Card className="bg-card border-border" data-testid="runs-list">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Chasses récentes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {runs.slice(0, 5).map((r) => (
                      <div key={r.id} className="flex items-center justify-between text-xs border border-border/40 rounded-md px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[9px] ${r.status === "done" ? "text-emerald-400 border-emerald-500/30" : r.status === "failed" ? "text-rose-400 border-rose-500/30" : "text-amber-400 border-amber-500/30"}`}>
                            {r.status === "done" ? "✓ Terminé" : r.status === "failed" ? "✗ Échec" : "⟳ En cours"}
                          </Badge>
                          <span className="text-muted-foreground">{new Date(r.started_at).toLocaleString("fr-FR")}</span>
                          {r.triggered_by === "daily_scheduler" && <Badge variant="outline" className="text-[9px]">Auto</Badge>}
                          {r.sector_focus && <Badge variant="outline" className="text-[9px]">{r.sector_focus}</Badge>}
                        </div>
                        <div className="text-muted-foreground">
                          {r.count_inserted ?? 0} ajoutés
                          {r.duplicates_skipped ? <span className="text-muted-foreground/60"> · {r.duplicates_skipped} doublons</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Leads list */}
            <Card className="bg-card border-border" data-testid="leads-list">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" /> Leads générés
                </CardTitle>
                <CardDescription>
                  {leads.length > 0 ? `${leads.length} derniers leads identifiés par l'IA. Cliquer sur l'entreprise pour ouvrir son site.` : "Aucun lead pour l'instant. Lance une chasse pour démarrer."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    <Radar className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>Clique sur "Lancer la chasse" pour générer tes premiers leads.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {leads.map((lead) => (
                      <div key={lead.id} className="border border-border rounded-lg p-3 hover:border-primary/40 hover:bg-primary/5 transition-colors" data-testid={`lead-${lead.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-semibold text-sm">{lead.entreprise}</span>
                              {typeof lead.lh_confidence === "number" && (
                                <Badge variant="outline" className={`text-[9px] ${lead.lh_confidence >= 80 ? "text-emerald-400 border-emerald-500/30" : "text-muted-foreground"}`}>
                                  {lead.lh_confidence}% confiance
                                </Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                              {lead.lh_ville && <span className="flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" /> {lead.lh_ville}</span>}
                              {lead.lh_taille && <span className="flex items-center gap-0.5"><Building2 className="h-2.5 w-2.5" /> {lead.lh_taille}</span>}
                              {lead.tags?.[0] && <span>· {lead.tags[0]}</span>}
                            </p>
                          </div>
                          {lead.lh_site && (
                            <a href={lead.lh_site} target="_blank" rel="noopener noreferrer" className="shrink-0">
                              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                            </a>
                          )}
                        </div>
                        <div className="mt-1.5 text-xs">
                          <span className="text-muted-foreground">{lead.poste} :</span> <b>{lead.prenom} {lead.nom}</b>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                          {lead.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}
                          {lead.telephone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.telephone}</span>}
                        </div>
                        {lead.notes && (
                          <p className="text-[11px] text-muted-foreground/80 mt-1.5 italic line-clamp-2">{lead.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-[10px] text-muted-foreground/60 text-center pt-2">
              ℹ️ Les emails sont générés selon un pattern usuel FR (prenom.nom@domaine.com) et ont un taux de rebond ~30%. Bouncer = lead à écarter et on apprend.
            </p>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
